<template>
  <div class="search-autocomplete">
    <!-- AUTOCOMPLETE INPUT -->
    <div class="autocomplete" v-click-outside="clickOutside">
      <div class="input-wrapper">
        <!-- SEARCH ICON -->
        <div
          class="search-autocomplete-icon"
          v-on:click="keyEnter"
          v-if="displayIcon"
        >
          <i class="fas fa-search fa-fw"></i>
        </div>

        <input
          :id="id"
          name="search"
          type="search"
          :class="{ classname, 'has-icon': displayIcon }"
          v-model="userInput"
          placeholder="I'm looking for ..."
          @input="fetchSearchSuggestions"
          @keydown.enter.prevent="keyEnter"
          @keydown.up.capture="keyUp"
          @keydown.down.capture="keyDown"
          @keydown.tab.prevent="keyEnter"
          @keydown.escape.prevent="keyEscape"
          autocomplete="off"
        />

        <!-- Loading when fetching the search result selected -->
        <div class="input-loading" v-if="fetchingDetails">
          <img
            alt="Loading while fetching the search result selected"
            src="https://storage.googleapis.com/wwf-australia-prod.appspot.com/website-public-assets/1938/dist/assets/img/loading-tail-spin.svg"
          />
        </div>
      </div>

      <div class="suggestions" v-if="displaySuggestions">
        <!-- LOADING -->
        <div class="suggestion-loading" v-if="isLoading">
          <img
            alt="Loading while fetching suggestions"
            src="https://storage.googleapis.com/wwf-australia-prod.appspot.com/website-public-assets/1938/dist/assets/img/loading-three-dots.svg"
          />
        </div>

        <!-- LIST OF SUGGESTIONS -->
        <div
          id="search-suggestion-list"
          class="suggestion-list"
          v-if="!isLoading && suggestions.length > 0"
        >
          <!-- List -->
          <div
            :id="'search-suggestion-item-' + p.index"
            class="suggestion-item"
            :class="{ 'item-active': cursor === p.index }"
            @click="selectSearchSuggestion(p.label)"
            @mouseover="cursor = p.index"
            v-for="p in suggestions"
          >
            {{ p.label }}
          </div>
        </div>

        <div
          class="suggestion-list"
          v-if="!isLoading && suggestions.length == 0"
        >
          <!-- Empty -->
          <div
            class="suggestion-item empty"
            @click="selectSearchSuggestion(userInput)"
          >
            No Suggestions
          </div>
        </div>
      </div>
    </div>

    <button
      type="button"
      class="search-button"
      v-if="displaySearchButton"
      v-on:click="selectSearchSuggestion(userInput)"
    >
      Search
    </button>
  </div>
</template>

<script>
export default {
  name: "SearchAutocomplete",

  props: {
    classname: String,
    id: String,
  },

  data() {
    return {
      userInput: "",
      suggestions: [],
      cursor: 1,
      displaySuggestions: false,
      isLoading: false,
      timeout: null,
      delay: 450, // ms
      minInputLength: 1,
      lastTimestamp: Date.now(),
      fetchingDetails: false,
    };
  },
  computed: {
    searchInput: {
      get() {
        return this.$store.getters.searchInput;
      },
      set(value) {
        this.$store.commit("setSearchInput", value);
      },
    },
  },
  props: {
    displayIcon: {
      type: Boolean,
      default: false,
    },
    displaySearchButton: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: "search-autocomplete-input",
    },
    classname: {
      type: String,
      default: "",
    },
    // De we redirect the user to the search result page or not after selecting a suggestion
    redirectOnSearch: {
      type: Boolean,
      default: true,
    },
  },
  watch: {
    searchInput(newVal, oldVal) {
      this.userInput = this.searchInput;
    },
  },

  mounted() {
    this.userInput = this.searchInput;
  },

  methods: {
    clickOutside() {
      this.displaySuggestions = false;
    },
    fetchSearchSuggestions() {
      if (this.timeout) {
        clearTimeout(this.timeout);
        this.timeout = null;
      }

      // In any case, if the input changed, the suggestions will change
      this.suggestions = [];

      if (this.userInput && this.userInput.length >= this.minInputLength) {
        this.isLoading = true;
        let timestamp = Date.now(); // timestamp for this api call
        this.lastTimestamp = timestamp; // 1 , 2 , 3
        this.timeout = setTimeout(() => {
          this.displaySuggestions = true;

          // Fetch Suggestions
          this.$store
            .dispatch("fetchSearchSuggestions", this.userInput)
            .then((response) => {
              if (response && timestamp >= this.lastTimestamp) {
                this.suggestions = response.map((e, idx) => ({
                  index: idx + 1,
                  label: e,
                }));

                this.isLoading = false;
              }
            })
            .catch((err) => {
              this.isLoading = false;
              console.error(err);
            });
        }, this.delay);
      } else {
        this.displaySuggestions = false;
        this.isLoading = false;
      }
    },
    selectSearchSuggestion(item) {
      this.userInput = item;
      this.searchInput = this.userInput;
      this.displaySuggestions = false;
      this.isLoading = false;
      this.suggestions = [];
      this.fetchingDetails = true;

      this.fetchingDetails = false;
      this.$emit("search", item);
      this.$store.commit("emptySelectedFilter");

      const inputElement = document.getElementById(this.id);
      if (inputElement) {
        inputElement.blur();
      }

      if (this.redirectOnSearch) {
        window.location.href = `/${wwfconfigs.SEARCH_RESULTS}?query=${item}`;
      }
    },
    keyUp(event) {
      if (this.displaySuggestions) {
        event.preventDefault();
        if (this.cursor > 1) {
          this.cursor -= 1;
          this.scrollToIndex(this.cursor);
        } else if (this.cursor === 1) {
          this.cursor = this.suggestions.length;
          this.scrollToIndex(this.cursor);
        }
      }
    },
    keyDown() {
      if (this.displaySuggestions) {
        event.preventDefault();
        if (this.cursor < this.suggestions.length) {
          this.cursor += 1;
          this.scrollToIndex(this.cursor);
        } else if (this.cursor === this.suggestions.length) {
          this.cursor = 1;
          this.scrollToIndex(this.cursor);
        }
      }
    },
    keyEnter() {
      if (this.displaySuggestions && this.suggestions[this.cursor - 1]) {
        this.selectSearchSuggestion(this.suggestions[this.cursor - 1].label);
        this.displaySuggestions = false;
      } else {
        this.selectSearchSuggestion(this.userInput);
      }
    },
    keyEscape() {
      this.displaySuggestions = false;
      this.suggestions = [];
    },
    scrollToIndex(index) {
      const listElement = document.getElementById(`search-suggestion-list`);
      const itemElement = document.querySelector(
        `#search-suggestion-list .suggestion-item`
      );
      const itemHeight = itemElement.offsetHeight;
      const scrollTop = listElement.scrollTop;
      const viewport = scrollTop + listElement.offsetHeight;
      const scrollOffset = itemHeight * index - itemHeight;

      if (scrollOffset < scrollTop || scrollOffset + itemHeight > viewport) {
        listElement.scrollTo({
          top: scrollOffset,
          behavior: "smooth",
        });
      }
    },
  },
};
</script>

<style scoped>
.search-autocomplete {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
}

.search-autocomplete-icon {
  position: absolute;
  top: 10px;
  left: 10px;
  height: 20px;
  width: 20px;
}

.input-wrapper {
  position: relative;
  width: 100%;
}

input {
  text-align: left;
  border-radius: 0;
  border: none;
  background-color: transparent;
  -webkit-box-shadow: none;
  -moz-box-shadow: none;
  box-shadow: none;
  font-size: 20px;
}

input:focus {
  border: none;
  background-color: transparent;
  -webkit-box-shadow: none;
  -moz-box-shadow: none;
  box-shadow: none;
}

input.has-icon {
  padding-left: 40px;
}

.search-button {
  margin-left: 2rem;
  padding: 15px 30px;
  background-color: var(--wwfOrange);
  color: var(--wwfWhite);
  text-transform: uppercase;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
}

.autocomplete {
  min-width: 300px;
  margin-bottom: 14px;
  border-bottom: 1px solid var(--wwfTransparentBlack015);
}

.autocomplete .suggestions {
  border-top: 1px solid var(--wwfGreyC8);
}

.autocomplete .suggestions .suggestion-list {
  /* 40 * 7 */
  max-height: 280px;
}

.autocomplete .suggestions .suggestion-list .suggestion-item {
  height: 40px;
  font-size: 16px;
}

@media only screen and (max-width: 450px) {
  .search-autocomplete {
    flex-wrap: nowrap;
  }

  .autocomplete {
    min-width: 150px;
    margin: unset;
  }

  .search-button {
    margin-left: 1rem;
    padding: 10px 16px;
    font-size: 14px;
  }
}
</style>


// WEBPACK FOOTER //
// searchAutocomplete.vue?ef952104